/**
 * Shared types for LLM integration layer.
 *
 * All provider communication, routing, and tracking types live here.
 * Consumed by agents, router, rate limiter, and cost tracker.
 */

/** Supported LLM providers. */
export type Provider = "gemini" | "fireworks" | "deepseek" | "claude" | "together" | "openai";

/** Task types for routing to specialized agents. */
export type TaskType = "narrative" | "mechanics" | "world" | "animation" | "idle";

/** A streaming text chunk from an LLM. */
export interface StreamChunk {
  /** The text content of this chunk. */
  text: string;
  /** Whether this is the final chunk in the stream. */
  done: boolean;
  /** Which provider generated this chunk. */
  provider: Provider;
}

/** Provider configuration with API key and optional base URL override. */
export interface ProviderConfig {
  provider: Provider;
  apiKey: string;
  /** Override base URL (used for Fireworks/DeepSeek OpenAI-compat endpoints). */
  baseUrl?: string;
}

/** Token usage tracking for a single request or aggregated session. */
export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  /** Estimated cost in USD. */
  estimatedCost: number;
}

/** Rate limit state for display and decision-making. */
export interface RateLimitState {
  requestsRemaining: number;
  tokensRemaining: number;
  resetsAt: Date;
}

/** Model tier classification for routing decisions. */
export type ModelTier = "pro" | "flash";

/** Default base URLs for OpenAI-compatible providers. */
export const PROVIDER_BASE_URLS: Partial<Record<Provider, string>> = {
  fireworks: "https://api.fireworks.ai/inference/v1",
  deepseek: "https://api.deepseek.com/v1",
  together: "https://api.together.xyz/v1",
  openai: "https://api.openai.com/v1",
};
